import * as Yup from "yup";
import { FC, useState } from "react";
import {
  ManifestDetailInterface,
  ManifestHeaderInterface,
  ManifestType,
} from "../../interfaces/ManifestInterface";
import classNames from "classnames";
import ManifestHeaderTable from "./ManifestHeaderTable";
import { FormDatePicker, FormTextInput } from "../../components/FormFields";
import moment from "moment";
import { PrimaryButton } from "../../components/Buttons";
import { useAppSelector } from "../../store/hooks";
import { alertService, loaderService } from "../../services";
import {
  getManifestDetail,
  getManifestHeader,
} from "../../services/manifestServices";
import { Formik } from "formik";
import ManifestDetailModal from "./ManifestDetailModal";

interface DispatchSearchFromValues {
  dispatchNumber: string;
  toDate: Date | null;
  fromDate: Date | null;
}

const DispatchGeneratedTable: FC = () => {
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);

  const [openModal, setOpenModal] = useState(false);
  const [dispatch, setDispatch] = useState<ManifestHeaderInterface>();
  const [details, setDetails] = useState<ManifestDetailInterface[]>([]);
  const [generatedDispatchList, setGeneratedDispatchList] = useState<
    ManifestHeaderInterface[]
  >([]);

  const validationSchema = Yup.object().shape({
    toDate: Yup.date().required("La fecha inicial es requerida"),
    fromDate: Yup.date().required("La fecha final es requerida"),
  });

  const handleSearch = async (values: DispatchSearchFromValues) => {
    const buCode = user.businessUnit?.code;

    if (!buCode) {
      alertService.error(
        "No hay ninguna tienda asociada a la sesión del usuario."
      );
      return;
    }

    loaderService.start();
    const dispatches = await getManifestHeader(
      buCode,
      values.fromDate,
      moment(values.toDate).endOf("days").toDate(),
      ManifestType.DESPACHO,
      values.dispatchNumber || null
    );
    loaderService.stop();

    if (dispatches === null || dispatches.length === 0) {
      setGeneratedDispatchList([]);
      return;
    }

    setGeneratedDispatchList(
      dispatches.map((d) => {
        const driverNames = d.drivers?.split(",");
        const driverIds = d.driversIdentificationNumber?.split(",");
        return {
          ...d,
          driver1Name: driverNames?.[0] ?? "",
          driver1IdentificationNumber: driverIds?.[0] ?? "",
          driver2Name: driverNames?.[1] ?? "",
          driver2IdentificationNumber: driverIds?.[1] ?? "",
          driver3Name: driverNames?.[2] ?? "",
          driver3IdentificationNumber: driverIds?.[2] ?? "",
        };
      })
    );
  };

  const handleDispatchClick = async (dispatch: ManifestHeaderInterface) => {
    const masterShipmentHeaderCode = dispatch.masterShipmentHeaderCode;

    if (loading) {
      return;
    }
    if (!masterShipmentHeaderCode) {
      alertService.error("Parece que el despacho no tiene un código asociado");
      return;
    }

    loaderService.start();
    const dispatchDetails = await getManifestDetail(
      masterShipmentHeaderCode,
      true
    );
    loaderService.stop();

    if (
      dispatchDetails === null ||
      !dispatchDetails[0] ||
      !dispatchDetails[0].manifestDetail
    ) {
      alertService.error("Hubo un error obteniendo los detalles del despacho");
      return;
    }

    const sorteredDispatchDetails = dispatchDetails[0].manifestDetail.sort(
      (a, b) => {
        const aValue = a.lineSeqNumber ?? -Infinity;
        const bValue = b.lineSeqNumber ?? -Infinity;

        return bValue - aValue;
      }
    );

    setDispatch(dispatch);
    setDetails(sorteredDispatchDetails);
    setOpenModal(true);
  };

  return (
    <div className="flex flex-1 flex-col bg-white gap-4 rounded-lg border px-6 py-6 w-full">
      <h1 className="text-2xl font-semibold text-gray-800 truncate mb-2">
        Despachos Generados
      </h1>
      <div
        className={classNames("flex flex-1 flex-col bg-white gap-4 rounded-lg")}
      >
        <Formik<DispatchSearchFromValues>
          initialValues={{
            dispatchNumber: "",
            toDate: new Date(),
            fromDate: moment().subtract(3, "days").toDate(),
          }}
          validationSchema={validationSchema}
          onSubmit={handleSearch}
        >
          {(formik) => (
            <form
              className="flex flex-row gap-4 mb-2 w-full"
              onSubmit={formik.handleSubmit}
            >
              <div className="w-full sm:w-1/3">
                <FormTextInput
                  id="dispatchNumber"
                  name="dispatchNumber"
                  value={formik.values.dispatchNumber}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "dispatchNumber",
                      e.target.value.toUpperCase()
                    )
                  }
                  label="Código de Manifiesto"
                  placeholder="Ingrese código de manifiesto"
                />
              </div>

              <div className="w-full sm:w-1/3">
                <FormDatePicker
                  id="fromDate"
                  name="fromDate"
                  label="Desde"
                  error={formik.errors.fromDate}
                  useRange={false}
                  maxDate={new Date()}
                  value={{
                    startDate: formik.values.fromDate,
                    endDate: formik.values.fromDate,
                  }}
                  toggleClassName={(oldClassname) =>
                    classNames(oldClassname, "text-indigo-600")
                  }
                  onChange={(e) =>
                    formik.setFieldValue(
                      "fromDate",
                      !!e?.startDate ? moment(e.startDate).toDate() : null
                    )
                  }
                  configs={{
                    shortcuts: {},
                  }}
                />
              </div>
              <div className="w-full sm:w-1/3">
                <FormDatePicker
                  id="toDate"
                  name="toDate"
                  label="Hasta"
                  error={formik.errors.toDate}
                  useRange={false}
                  maxDate={new Date()}
                  value={{
                    startDate: formik.values.toDate,
                    endDate: formik.values.toDate,
                  }}
                  toggleClassName={(oldClassname) =>
                    classNames(oldClassname, "text-indigo-600")
                  }
                  onChange={(e) =>
                    formik.setFieldValue(
                      "toDate",
                      !!e?.startDate ? moment(e.startDate).toDate() : null
                    )
                  }
                  configs={{
                    shortcuts: {},
                  }}
                />
              </div>

              <div className="w-1/12 mt-8">
                <PrimaryButton className="!px-6 !py-2" disabled={loading}>
                  Buscar
                </PrimaryButton>
              </div>
            </form>
          )}
        </Formik>

        <ManifestHeaderTable
          manifests={generatedDispatchList}
          onManifestClick={handleDispatchClick}
        />
      </div>

      {dispatch && (
        <ManifestDetailModal
          showPiece
          openModal={openModal}
          setOpenModal={setOpenModal}
          manifest={dispatch}
          detailData={details}
        />
      )}
    </div>
  );
};

export default DispatchGeneratedTable;
